import {
  DashboardBreakpoint,
  PlotLayoutItem,
  PlotLayoutsByBreakpoint,
} from "@/types/tableState.types";
import { VivoPlotProps } from "@/components/VivoPlots/types";

export const GRID_BREAKPOINTS: Record<DashboardBreakpoint, number> = {
  lg: 1200,
  md: 996,
  sm: 768,
  xs: 0,
};

export const GRID_COLUMNS: Record<DashboardBreakpoint, number> = {
  lg: 12,
  md: 10,
  sm: 6,
  xs: 2,
};

export const GRID_WIDTHS = { small: 4, medium: 6, large: 12 } as const;
export const GRID_HEIGHTS = { small: 4, medium: 5, large: 6 } as const;
export const GRID_ROW_HEIGHT = 88;
export const GRID_MARGIN: [number, number] = [16, 16];

const TOTAL_GRID_UNITS = 12;
const MIN_LAYOUT_HEIGHT = 3;

export const getPlotId = (plot: VivoPlotProps, index: number) =>
  plot.config.plotId || `plot-${index}`;

export const getPlotSize = (plotSize?: string): keyof typeof GRID_WIDTHS =>
  plotSize === "medium" || plotSize === "large" ? plotSize : "small";

export const buildDefaultLayout = (
  plots: VivoPlotProps[],
  breakpoint: DashboardBreakpoint,
  startY = 0,
): PlotLayoutItem[] => {
  const columns = GRID_COLUMNS[breakpoint];
  let x = 0;
  let y = startY;
  let rowHeight = 0;

  return plots.map((plot, index) => {
    const size = getPlotSize(plot.config.plotSize);
    const width = Math.max(
      1,
      Math.min(
        columns,
        Math.round((GRID_WIDTHS[size] / TOTAL_GRID_UNITS) * columns),
      ),
    );
    const height = GRID_HEIGHTS[size];

    if (x + width > columns) {
      y += rowHeight;
      x = 0;
      rowHeight = 0;
    }

    const item = {
      i: getPlotId(plot, index),
      x,
      y,
      w: width,
      h: height,
      minW: Math.min(2, columns),
      minH: MIN_LAYOUT_HEIGHT,
    };
    x += width;
    rowHeight = Math.max(rowHeight, height);
    return item;
  });
};

export const compactLayoutItems = (
  items: PlotLayoutItem[],
  columns: number,
): PlotLayoutItem[] => {
  const ordered = [...items].sort((a, b) => a.y - b.y || a.x - b.x);
  let x = 0;
  let y = 0;
  let rowHeight = 0;

  return ordered.map((item) => {
    if (x + item.w > columns) {
      y += rowHeight;
      x = 0;
      rowHeight = 0;
    }

    const packed = { ...item, x, y };
    x += item.w;
    rowHeight = Math.max(rowHeight, item.h);
    return packed;
  });
};

export const compactLayouts = (
  layouts: PlotLayoutsByBreakpoint,
): PlotLayoutsByBreakpoint =>
  (Object.keys(GRID_BREAKPOINTS) as DashboardBreakpoint[]).reduce(
    (result, breakpoint) => {
      const items = layouts[breakpoint];
      if (items) {
        result[breakpoint] = compactLayoutItems(
          items,
          GRID_COLUMNS[breakpoint],
        );
      }
      return result;
    },
    {} as PlotLayoutsByBreakpoint,
  );

export const isValidLayoutItem = (item: PlotLayoutItem): boolean =>
  Number.isFinite(item?.x) &&
  Number.isFinite(item?.y) &&
  Number.isFinite(item?.w) &&
  Number.isFinite(item?.h);

export const normalizeLayouts = (
  plots: VivoPlotProps[],
  savedLayouts?: PlotLayoutsByBreakpoint,
): PlotLayoutsByBreakpoint =>
  (Object.keys(GRID_BREAKPOINTS) as DashboardBreakpoint[]).reduce(
    (layouts, breakpoint) => {
      const columns = GRID_COLUMNS[breakpoint];
      const savedById = new Map(
        (savedLayouts?.[breakpoint] || [])
          .filter(isValidLayoutItem)
          .map((item) => [item.i, item]),
      );

      const existingItems: PlotLayoutItem[] = [];
      const newPlots: VivoPlotProps[] = [];

      plots.forEach((plot, index) => {
        const id = getPlotId(plot, index);
        const saved = savedById.get(id);
        if (!saved) {
          newPlots.push(plot);
          return;
        }
        const width = Math.max(1, Math.min(saved.w, columns));
        existingItems.push({
          ...saved,
          i: id,
          w: width,
          h: Math.max(MIN_LAYOUT_HEIGHT, saved.h),
          x: Math.max(0, Math.min(saved.x, columns - width)),
          y: Math.max(0, saved.y),
          static: false,
          isDraggable: true,
          isResizable: true,
        });
      });

      // New plots (no saved position yet) start right after the bottom edge
      // of the existing saved layout, not from an independent from-scratch
      // packing pass — otherwise they land wherever a full default repack of
      // every plot would have put them, disconnected from where the saved
      // plots actually are.
      const startY = existingItems.reduce(
        (max, item) => Math.max(max, item.y + item.h),
        0,
      );

      layouts[breakpoint] = [
        ...existingItems,
        ...buildDefaultLayout(newPlots, breakpoint, startY),
      ];
      return layouts;
    },
    {} as PlotLayoutsByBreakpoint,
  );
